import React, { lazy, Suspense, memo, useEffect, useMemo } from "react";
import { matchPath, useLocation } from "react-router-dom";
import Loading from "components/shared-components/Loading";
import Views from "views";
import { themeStore } from "stores/themeStore.store";
import { authStore } from "stores/auth.store";
import { adminStore } from "stores/admin.store";
import { userService } from "services/v2/UserService";
import { buildUserProfile } from "utils/userProfile";
import { seedCsrfToken } from "services/v2/csrfService";
import {
  backOfficeRoutes,
  customerRoutes,
  commonRoutes,
  publicRoutes,
  sharedPublicRoutes,
} from "configs/RoutesConfig";

const AppLayout = lazy(() => import("./AppLayout"));
const AuthLayout = lazy(() => import("./AuthLayout"));
const BlankAppLayout = lazy(() => import("./BlankLayout"));

export const getMatchedRoute = (pathname, userType) => {
  const activeRoleRoutes =
    userType === "backOffice" ? backOfficeRoutes : customerRoutes;
  const activeRoutes = [
    ...commonRoutes,
    ...activeRoleRoutes,
    ...publicRoutes,
    ...sharedPublicRoutes,
  ];
  return activeRoutes.find((route) =>
    matchPath({ path: route.path, end: true }, pathname),
  );
};

const LAYOUT_MAP = {
  auth: AuthLayout,
  blank: BlankAppLayout,
  app: AppLayout,
};

const Layouts = () => {
  const { isAuthenticated, authChecked, setAuthenticated, setAuthChecked } =
    authStore();
  const { userProfile, setUserProfile } = adminStore();
  const { navCollapsed, navType, direction } = themeStore();
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    adminStore.getState().clearProfileLoaded();
    const bootstrap = async () => {
      // Seed CSRF cookie first, so any subsequent POST (e.g. refresh-on-401)
      try {
        await seedCsrfToken();
      } catch (err) {
        console.error("CSRF seed failed, continuing anyway", err);
      }
      if (cancelled) return;

      try {
        const profile = await userService.getUserProfile();
        if (cancelled) return;

        setUserProfile(buildUserProfile(profile.data));
        setAuthenticated(true);
      } catch {
        if (!cancelled) setAuthenticated(false);
      } finally {
        if (!cancelled) setAuthChecked(true);
      }
    };

    bootstrap();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const matchedRoute = useMemo(
    () => getMatchedRoute(location.pathname, userProfile?.userType),
    [location.pathname, userProfile?.userType],
  );

  if (!authChecked) {
    return <Loading cover="page" />;
  }

  const layoutKey = !isAuthenticated
    ? "auth"
    : matchedRoute?.meta?.blankLayout
      ? "blank"
      : "app";
  const Layout = LAYOUT_MAP[layoutKey];
  return (
    <Suspense fallback={<Loading cover="content" />}>
      <Layout
        direction={direction}
        navType={navType}
        navCollapsed={navCollapsed}
      >
        <Views />
      </Layout>
    </Suspense>
  );
};

export default memo(Layouts);
